import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import useSessionStore from '../store/sessionStore'
import useWebRTC from '../hooks/useWebRTC'
import useSignaling from '../hooks/useSignaling'
import { QRCodeSVG } from 'qrcode.react'
import QRTransfer from '../components/transfer/QRTransfer'
import PhoneMirror from '../components/transfer/PhoneMirror'
import Button from '../components/ui/Button'
import Badge from '../components/ui/Badge'
import Card from '../components/ui/Card'
import styles from './Connect.module.css'

export default function Connect() {
  const navigate = useNavigate()
  const { setPhoneConnected, setWebcamActive, webcamActive, capturedFiles } = useSessionStore()
  const [connectionState, setConnectionState] = useState('waiting')
  const [roomCode, setRoomCode] = useState('')
  const [remoteStream, setRemoteStream] = useState(null)
  const [localStream, setLocalStream] = useState(null)
  const [role, setRole] = useState(null)
  const roleRef = useRef(null)
  const [secondsLeft, setSecondsLeft] = useState(15 * 60)
  const [showTransferOptions, setShowTransferOptions] = useState(false)
  const dataChannelRef = useRef(null)
  const rtcStartedRef = useRef(false)

  const webrtc = useWebRTC({
    onRemoteStream: (stream) => {
      setRemoteStream(stream)
      setConnectionState('active')
      setPhoneConnected(true)
    },
    onDataChannel: (data) => {
      // handle incoming data channel messages if needed
    },
    onConnectionEnded: () => {
      setConnectionState('ended')
      setPhoneConnected(false)
    },
    onConnectionConnected: () => {
      setConnectionState('active')
      setPhoneConnected(true)
    },
  })

  const signaling = useSignaling({
    roomCode,
    onMessage: (msg) => {
      if (msg.type === 'assigned_role') {
        console.log('[Connect] Assigned role:', msg.role)
        setRole(msg.role)
        roleRef.current = msg.role
      }
      if (msg.type === 'peer_connected') {
        console.log('[Connect] Peer connected, role:', roleRef.current)
        setConnectionState('establishing')
      }
      if (msg.type === 'ready') {
        console.log('[Connect] Phone is ready, starting WebRTC')
        if (roleRef.current === 'laptop') {
          startWebRTC()
        }
      }
      if (msg.type === 'peer_disconnected') {
        console.log('[Connect] Peer disconnected')
        rtcStartedRef.current = false
        setConnectionState('ended')
        setPhoneConnected(false)
        webrtc.close()
      }
      if (msg.type === 'offer') {
        console.log('[Connect] Received offer, handling as answerer')
        webrtc.handleOffer?.(
          msg,
          (answer) => {
            console.log('[Connect] Sending answer')
            signaling.send({ type: 'answer', sdp: answer.sdp })
          },
          (candidate) => {
            console.log('[Connect] Sending ICE candidate from answer side')
            signaling.send({ type: 'ice_candidate', candidate })
          }
        )
      }
      if (msg.type === 'answer') {
        console.log('[Connect] Received answer, calling handleAnswer')
        webrtc.handleAnswer?.(msg)
      }
      if (msg.type === 'ice_candidate') {
        console.log('[Connect] Received ICE candidate', JSON.stringify(msg.candidate))
        webrtc.handleIceCandidate?.(msg.candidate)
      }
    },
  })

  useEffect(() => {
    const code = Math.floor(100000 + Math.random() * 900000).toString()
    setRoomCode(code)
  }, [])

  useEffect(() => {
    if (roomCode) {
      signaling.connect()
    }
    return () => {
      signaling.disconnect()
      webrtc.close()
    }
  }, [roomCode])

  useEffect(() => {
    const interval = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval)
          return 0
        }
        return prev - 1
      })
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  const startWebRTC = useCallback(async () => {
    if (rtcStartedRef.current) {
      console.log('[Connect] WebRTC already started, skipping duplicate')
      return
    }
    rtcStartedRef.current = true
    console.log('[Connect] Starting WebRTC as offerer')
    try {
      webrtc.createPeer((candidate) => {
        if (candidate) {
          console.log('[Connect] Sending ICE candidate')
          signaling.send({ type: 'ice_candidate', candidate })
        }
      })
      const channel = webrtc.createDataChannel('control')
      channel.onopen = () => {
        console.log('[Connect] DataChannel open')
        setConnectionState('active')
        setPhoneConnected(true)
      }
      dataChannelRef.current = channel

      const offer = await webrtc.createOffer()
      if (!offer) {
        throw new Error('Failed to create offer')
      }
      signaling.send({ type: 'offer', sdp: offer.sdp })
      console.log('[Connect] Offer sent')
    } catch (err) {
      console.error('[Connect] startWebRTC failed:', err)
      setConnectionState('ended')
    }
  }, [webrtc, signaling])

  const toggleWebcam = useCallback(async () => {
    if (!webcamActive) {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false })
      setLocalStream(stream)
      webrtc.addLocalStream(stream)
      setWebcamActive(true)
    } else {
      if (localStream) {
        localStream.getTracks().forEach((t) => t.stop())
      }
      setWebcamActive(false)
    }
  }, [webcamActive, localStream, webrtc, setWebcamActive])

  const sendSignal = useCallback(
    (data) => {
      if (dataChannelRef.current && dataChannelRef.current.readyState === 'open') {
        dataChannelRef.current.send(JSON.stringify(data))
      } else {
        signaling.send(data)
      }
    },
    [signaling]
  )

  const handleDisconnect = () => {
    signaling.disconnect()
    webrtc.close()
    if (localStream) {
      localStream.getTracks().forEach((t) => t.stop())
    }
    setConnectionState('ended')
    setPhoneConnected(false)
    setWebcamActive(false)
  }

  const handleDownload = (file) => {
    if (!file.processedBlob) return
    const url = URL.createObjectURL(file.processedBlob)
    const a = document.createElement('a')
    a.href = url
    a.download = file.originalName
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const pairingUrl = `${window.location.origin}/connect?room=${roomCode}`

  const formatTime = (s) => {
    const m = Math.floor(s / 60)
    const sec = s % 60
    return `${m}:${sec.toString().padStart(2, '0')}`
  }

  const isLow = secondsLeft < 2 * 60

  const uploadedFiles = capturedFiles.filter((f) => f.status === 'done' && f.transferUrl)

  const renderFileTransfer = () => (
    <div className={styles.transferSection}>
      <h3 className={styles.transferTitle}>File Transfer</h3>
      {uploadedFiles.length === 0 ? (
        <p className={styles.transferEmpty}>No uploaded files available.</p>
      ) : (
        <div className={styles.transferList}>
          {uploadedFiles.map((file) => (
            <Card key={file.id} padding="md" className={styles.transferCard}>
              <div className={styles.transferFileInfo}>
                <span className={styles.transferFileName}>{file.originalName}</span>
                <span className={styles.transferFileMeta}>
                  {(file.sizeBytes / 1024 / 1024).toFixed(2)} MB
                </span>
              </div>
              <div className={styles.transferQrWrap}>
                <QRCodeSVG value={file.transferUrl} size={160} />
                <p className={styles.transferQrNote}>Scan to receive</p>
              </div>
              <Button
                variant="secondary"
                size="sm"
                fullWidth
                onClick={() => handleDownload(file)}
              >
                Download to laptop
              </Button>
            </Card>
          ))}
        </div>
      )}
    </div>
  )

  return (
    <main className={styles.main}>
      <div className={styles.container}>
        {connectionState === 'waiting' && (
          <div className={styles.state}>
            {!showTransferOptions ? (
              <>
                <h1 className={styles.title}>Waiting to pair</h1>
                <p className={styles.sub}>
                  Open the VerifyLink app on your Android phone and scan the QR code or enter the 6-digit code below.
                </p>
                <QRTransfer url={pairingUrl} code={roomCode} />
                <div className={styles.countdown}>
                  <span className={styles.countdownLabel}>Files expire in</span>
                  <span className={isLow ? styles.countdownLow : styles.countdownValue}>
                    {formatTime(secondsLeft)}
                  </span>
                </div>
                <div className={styles.pulse}>
                  <span className={styles.pulseDot} aria-hidden="true" />
                  <span>Waiting for your phone...</span>
                </div>
                <button
                  className={styles.skipLink}
                  onClick={() => setShowTransferOptions(true)}
                >
                  Skip phone connection � just transfer files
                </button>
              </>
            ) : (
              <>
                <h1 className={styles.title}>Transfer files</h1>
                <p className={styles.sub}>
                  Your files are ready. Scan a QR code or download directly.
                </p>
                {renderFileTransfer()}
                <div className={styles.actions}>
                  <Button variant="ghost" onClick={() => setShowTransferOptions(false)}>
                    Back to phone connection
                  </Button>
                </div>
              </>
            )}
          </div>
        )}

        {connectionState === 'establishing' && (
          <div className={styles.state}>
            <div className={styles.checkmark}>
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                <circle cx="24" cy="24" r="22" stroke="var(--color-success)" strokeWidth="2" />
                <path d="M14 24l7 7 13-13" stroke="var(--color-success)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h1 className={styles.title}>Phone connected</h1>
            <div className={styles.spinnerWrap}>
              <span className={styles.spinner} aria-hidden="true" />
              <span>Setting up secure connection...</span>
            </div>
          </div>
        )}

        {connectionState === 'active' && (
          <div className={styles.activeWrap}>
            <div className={styles.leftPanel}>
              <PhoneMirror
                remoteStream={remoteStream}
                onDisconnect={handleDisconnect}
                sendSignal={sendSignal}
                phoneDimensions={{ width: 1080, height: 1920 }}
              />
            </div>
            <div className={styles.rightPanel}>
              <div className={styles.statusRow}>
                <div className={styles.status}>
                  <span className={styles.statusDot} aria-hidden="true" />
                  <span>Connected</span>
                </div>
                <Button variant="danger" size="sm" onClick={handleDisconnect}>
                  Disconnect
                </Button>
              </div>

              <div className={styles.controlGroup}>
                <span className={styles.controlLabel}>Webcam</span>
                <Button
                  variant={webcamActive ? 'primary' : 'secondary'}
                  size="sm"
                  onClick={toggleWebcam}
                >
                  {webcamActive ? 'Pause' : 'Start'}
                </Button>
              </div>

              <div className={styles.statusBadge}>
                <Badge variant="success">
                  {webcamActive ? 'Camera replacement active' : 'Camera replacement ready'}
                </Badge>
              </div>

              {localStream && (
                <div className={styles.webcamPreview}>
                  <p className={styles.previewLabel}>What your phone's camera sees</p>
                  <video
                    autoPlay
                    playsInline
                    muted
                    ref={(el) => {
                      if (el && localStream) el.srcObject = localStream
                    }}
                    className={styles.previewVideo}
                  />
                </div>
              )}

              <div className={styles.countdownCard}>
                <span className={styles.countdownLabel}>Files expire in</span>
                <span className={isLow ? styles.countdownLow : styles.countdownValue}>
                  {formatTime(secondsLeft)}
                </span>
              </div>

              {renderFileTransfer()}

              <div className={styles.instructions}>
                <h3 className={styles.instructionsTitle}>How to complete verification</h3>
                <ol className={styles.instructionsList}>
                  <li>Click on the phone screen to navigate.</li>
                  <li>Open your verification app on the phone.</li>
                  <li>When the app opens the camera, it will use your laptop webcam.</li>
                  <li>Complete the verification as normal.</li>
                </ol>
              </div>

              <div className={styles.shortcuts}>
                <p className={styles.shortcutsTitle}>Keyboard shortcuts</p>
                <ul className={styles.shortcutsList}>
                  <li>Esc � Go back</li>
                  <li>Home � Home screen</li>
                  <li>Arrow keys � Scroll</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {connectionState === 'ended' && (
          <div className={styles.state}>
            <h1 className={styles.title}>Connection ended</h1>
            <p className={styles.sub}>Your phone has been disconnected.</p>
            <div className={styles.actions}>
              <Button onClick={() => window.location.reload()}>Reconnect</Button>
              <Button variant="ghost" onClick={() => navigate('/finish')}>
                Back to finish
              </Button>
            </div>
          </div>
        )}
      </div>
    </main>
  )
}




